import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Sparkles, RotateCcw, FastForward, PlayCircle } from 'lucide-react';
import PIELayout from '../PIELayout';
import { useFrontlineDemo, patch, resetDemo, advanceDemo, STATES, COACH_QA, WORKER, MANAGER, jobsOf } from './frontlineDemo';
import { JobCard, AddJobDialog } from './JobIntake';
import { Plus } from 'lucide-react';
import { Today, GetSupport, Validate, MyEvidence } from './WorkerViews';
import { MyTeam, Requests, Observe, Patterns } from './ManagerViews';
import { Card, Btn, Chip } from './bits';
import { GuidedTour } from './GuidedTour';
import './frontline.css';

const WORKER_TABS = [['today', 'Today'], ['work', 'My work'], ['support', 'Get support'], ['validate', 'Did it work?'], ['evidence', 'My evidence'], ['coach', 'Coach']];
const MANAGER_TABS = [['team', 'My team'], ['requests', 'Support requests'], ['observe', 'Observe'], ['patterns', 'Patterns'], ['coach', 'Coach']];

function Coach({ persona }) {
  const [q, setQ] = useState(null);
  const qa = COACH_QA[persona];
  return (
    <div className="fl-stack" data-testid="fl-coach">
      <header className="fl-hero"><p className="pie-eyebrow">Coach · frontline</p><h1>{persona === 'worker' ? 'Ask Mirror about your next move' : 'Ask Mirror where to coach'}</h1><p className="fl-sub">Deterministic demo answers for this scenario. The full Mirror Coach is unchanged under Coach in the main navigation.</p></header>
      <div className="fl-row fl-chips">{qa.map(([question]) => <Chip key={question} active={q === question} onClick={() => setQ(question)} testId={`fl-coach-q-${qa.findIndex((x) => x[0] === question)}`}>{question}</Chip>)}</div>
      {q && <Card title="Mirror" tag={<Sparkles size={14} className="fl-accent" />} className="fl-insight" testId="fl-coach-answer"><p>{qa.find((x) => x[0] === q)[1]}</p></Card>}
    </div>
  );
}

function MyWork({ s }) {
  const [add, setAdd] = useState(false);
  const mine = jobsOf(s).filter((j) => j.tech === WORKER.name);
  const open = mine.filter((j) => j.status !== 'done'), done = mine.filter((j) => j.status === 'done');
  return (
    <div className="fl-stack" data-testid="fl-my-work">
      <header className="fl-hero"><p className="pie-eyebrow">My work</p><h1>Today's jobs</h1><p className="fl-sub">{open.length} open · {open.filter((j) => j.type !== 'pump-service' && j.type !== 'calibration').length} application opportunit{open.length === 1 ? 'y' : 'ies'}</p></header>
      <div className="fl-row"><Btn primary onClick={() => setAdd(true)} testId="fl-add-job-btn"><Plus size={14} /> Add a job</Btn><span className="fl-small" style={{ margin: 0 }}>Enter manually or scan the asset's QR tag</span></div>
      {open.map((j) => <JobCard key={j.wo} job={j} s={s} />)}
      {done.map((j) => <JobCard key={j.wo} job={j} s={s} />)}
      {add && <AddJobDialog onClose={() => setAdd(false)} />}
    </div>
  );
}

export default function FrontlineDemoPage() {
  const s = useFrontlineDemo();
  const navigate = useNavigate();
  const { persona: pParam, tab: tParam } = useParams();
  const persona = pParam === 'manager' ? 'manager' : 'worker';
  const tabs = persona === 'worker' ? WORKER_TABS : MANAGER_TABS;
  const tab = tabs.some(([k]) => k === tParam) ? tParam : tabs[0][0];
  useEffect(() => { if (s.persona !== persona) patch({ persona }); }, [persona, s.persona]);
  const go = (t) => navigate(`/mirror/frontline/${persona}/${t}`);
  const switchTo = (p) => navigate(`/mirror/frontline/${p}/${p === 'worker' ? (s.state >= 5 && s.state < 7 ? 'validate' : 'today') : (s.state >= 3 && s.state < 5 ? 'requests' : 'team')}`);
  const showDev = process.env.NODE_ENV !== 'production' || new URLSearchParams(window.location.search).has('demo');
  const [tour, setTour] = useState(() => new URLSearchParams(window.location.search).has('tour'));
  const exitTour = useCallback(() => setTour(false), []);

  const view = {
    today: <Today s={s} go={go} />, work: <MyWork s={s} />, support: <GetSupport s={s} go={go} />, validate: <Validate s={s} go={go} />, evidence: <MyEvidence s={s} />,
    team: <MyTeam s={s} go={go} />, requests: <Requests s={s} go={go} />, observe: <Observe s={s} go={go} />, patterns: <Patterns />, coach: <Coach persona={persona} />,
  }[tab];

  return (
    <PIELayout narrow>
      <div className="fl-shell" data-testid="fl-shell" data-state={s.state} data-persona={persona}>
        <div className="fl-topbar">
          <div className="fl-row" style={{ alignItems: 'center' }}>
            <span className="fl-demo-pill" data-testid="fl-demo-pill">Frontline demo</span>
            {!tour && <button type="button" className="fl-tour-start" onClick={() => setTour(true)} data-testid="fl-tour-start"><PlayCircle size={14} /> Guided tour · 90s</button>}
          </div>
          <div className="fl-persona" role="tablist" aria-label="Demo persona">
            {[['worker', WORKER.name, WORKER.role], ['manager', MANAGER.name, MANAGER.role]].map(([p, n, r]) => (
              <button key={p} type="button" role="tab" aria-selected={persona === p} className={`fl-persona-btn ${persona === p ? 'fl-persona-active' : ''}`} onClick={() => switchTo(p)} data-testid={`fl-persona-${p}`}><strong>{n}</strong><span>{r}</span></button>
            ))}
          </div>
        </div>
        <nav className="fl-nav" aria-label="Frontline navigation">
          {tabs.map(([k, l]) => <button key={k} type="button" className={`fl-nav-link ${tab === k ? 'fl-nav-active' : ''}`} onClick={() => go(k)} data-testid={`fl-nav-${k}`}>{l}{k === 'validate' && s.state >= 5 && s.state < 7 && <i className="fl-dot" />}{k === 'requests' && s.state >= 3 && s.state < 5 && <i className="fl-dot" />}</button>)}
        </nav>
        {view}
        {tour && <GuidedTour onExit={exitTour} />}
        {showDev && (
          <div className="fl-devbar" data-testid="fl-devbar">
            <span className="fl-small">Demo state {s.state}/11 · {STATES[s.state - 1]}</span>
            <div className="fl-row"><Btn onClick={advanceDemo} testId="fl-advance-demo"><FastForward size={13} /> Advance</Btn><Btn onClick={() => { resetDemo(); navigate('/mirror/frontline/worker/today'); }} testId="fl-reset-demo"><RotateCcw size={13} /> Reset demo</Btn></div>
          </div>
        )}
      </div>
    </PIELayout>
  );
}
